import { Paper, Stack, Typography } from '@mui/material'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, useMinimalsTheme } from '@tom-ui/ui'
import { formatDateTime, useDateFormat } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'

type VisitTimingsProps = {
  vesselVisitItem: IVesselVisitItem
  minutesLeft: number
}

export const VisitTimings: FC<VisitTimingsProps> = observer(props => {
  const { vesselVisitItem, minutesLeft } = props
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const { formatMinutesToHoursAndMinutes } = useDateFormat()

  const ata = vesselVisitItem?.data.ata && formatDateTime(vesselVisitItem?.data.ata)
  const atb = vesselVisitItem?.data.atb && formatDateTime(vesselVisitItem?.data.atb)
  const operationsStart =
    vesselVisitItem?.data.operationStart && formatDateTime(vesselVisitItem?.data.operationStart)
  const operationsEnd =
    vesselVisitItem?.data.operationEnd && formatDateTime(vesselVisitItem?.data.operationEnd)
  const estimatedOperationsEnd = formatDateTime(dayjs().add(minutesLeft, 'minute').toDate())
  const atd = vesselVisitItem?.data.atd && formatDateTime(vesselVisitItem?.data.atd)

  const isCompleted =
    vesselVisitItem?.data &&
    (vesselVisitItem?.data?.status === 'Closed' || vesselVisitItem?.data?.status === 'Departed')

  const operationsEndOrEstimated = isCompleted ? operationsEnd : estimatedOperationsEnd

  const totalBerthTimeMinutes =
    vesselVisitItem?.data.atb && vesselVisitItem?.data.atd
      ? dayjs(vesselVisitItem.data.atd).diff(dayjs(vesselVisitItem.data.atb), 'minute')
      : null
  const totalBerthTimeFormatted = formatMinutesToHoursAndMinutes(totalBerthTimeMinutes)

  const grossWorkingTimeMinutes =
    vesselVisitItem?.data.ata && vesselVisitItem?.data.atd
      ? dayjs(vesselVisitItem.data.atd).diff(dayjs(vesselVisitItem.data.ata), 'minute')
      : null
  const grossWorkingTimeFormatted = formatMinutesToHoursAndMinutes(grossWorkingTimeMinutes)

  return (
    <Paper
      sx={{
        padding: theme.customSpacing.m,
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: theme.customSpacing.m,
      }}
    >
      <Stack width='40%'>
        <Typography variant='h6' marginBottom={theme.customSpacing.m}>
          {t('visitTimings', 'Visit Timings')}
        </Typography>
        <Stack direction='row' spacing={theme.customSpacing.m}>
          <Stack direction='column'>
            <Stack direction='row' spacing={theme.customSpacing.xs}>
              <Typography variant='subtitle2'>{t('ata ', 'ATA')}</Typography>
              <Typography variant='body2'>{ata ?? EmptyIndicator}</Typography>
            </Stack>
            <Stack direction='row' spacing={theme.customSpacing.xs}>
              <Typography variant='subtitle2'>{t('atb', 'ATB')}</Typography>
              <Typography variant='body2'>{atb ?? EmptyIndicator}</Typography>
            </Stack>
            <Stack direction='row' spacing={theme.customSpacing.xs}>
              <Typography variant='subtitle2'>{t('startOfOps', 'Start of ops')}</Typography>
              <Typography variant='body2'>{operationsStart ?? EmptyIndicator}</Typography>
            </Stack>
            <Stack direction='row' spacing={theme.customSpacing.xs}>
              <Typography variant='subtitle2'>
                {t('endOfOps', 'End of ops')}{' '}
                {isCompleted ? '' : `(${t('estimated', 'estimated')})`}
              </Typography>
              <Typography variant='body2'>{operationsEndOrEstimated ?? EmptyIndicator}</Typography>
            </Stack>
            <Stack direction='row' spacing={theme.customSpacing.xs}>
              <Typography variant='subtitle2'>{t('atd', 'ATD')}</Typography>
              <Typography variant='body2'>{atd ?? EmptyIndicator}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={theme.customSpacing.m} width='60%'>
        <Paper
          sx={{ bgcolor: theme.palette.grey[100], padding: theme.customSpacing.s, width: '50%' }}
        >
          <Typography variant='caption'>{t('totalBethTime', 'Total berth time')}</Typography>
          <Typography variant='h4'>
            {isCompleted ? totalBerthTimeFormatted : EmptyIndicator}
          </Typography>
        </Paper>
        <Paper
          sx={{ bgcolor: theme.palette.grey[100], padding: theme.customSpacing.s, width: '50%' }}
        >
          <Typography variant='caption'>{t('grossWorkingTime', 'Gross Working Time')}</Typography>
          <Typography variant='h4'>
            {isCompleted ? grossWorkingTimeFormatted : EmptyIndicator}
          </Typography>
        </Paper>
      </Stack>
    </Paper>
  )
})
